import * as React from 'react';
import { withRouter, Link, Redirect } from 'react-router-dom';
import bindAllOfThis from "src/utils/BindThisHelper";
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';
import "./Users.less";

import DataTable, {
    DataTableColumnProps,
    Renderer,
    Filterer,
    DataTableButton,
    DataTableButtonReset
} from 'src/components/common/DataTable';

import TableRequestDTO from 'src/models/datatable/TableRequestDTO';
import TableResponseDTO from 'src/models/datatable/TableResponseDTO';
import UserDTO from 'src/models/user/UserDTO';
import UserApiController from 'src/api/UserApiController';
import ReportApiController from 'src/api/ReportApiController';
import DistrictApiController from 'src/api/DistrictApiController';
import { message, Button, Upload, Icon, Tooltip, Dropdown, Menu, Select, Divider, Spin, Row, Col, Modal } from 'antd';
import { downloadFile } from '../../utils/DownloadUtil';
import { UserState } from 'src/redux/UserReducer';
import { ReduxStoreModel } from 'src/redux/ReduxModel';
import { connect } from 'react-redux';
import { Roles } from 'src/constants/Roles';
import { UploadChangeParam, RcFile } from "antd/lib/upload/interface";
import GenericBaseResponseDTO from 'src/models/validation/GenericBaseResponseDTO';
import UsersUploadErrorModal from './UsersUploadErrorModal';
import { AddEditUserStateParams } from './AddEditUser';
import ExcelRowValidationResultDTO from 'src/models/validation/ExcelRowValidationResultDTO';

interface RouteParams {
    isAdminTable: string;
}

export interface UsersProps extends PageProps<RouteParams>, LocalizeContextProps {
    classroomId: number;
    onlyClassroomUsers: boolean;
    shouldRefreshTable: boolean;
    updateShouldRefreshTable: () => void;
    User: UserState;
}

interface UsersState {
    loading: boolean;
    tableRecords: UserDTO[];
    isAdminTable: boolean;       // this is here so we can set a default value in the constructor
    classroomId: number;         // this is here so we can set a default value in the constructor
    onlyClassroomUsers: boolean; // this is here so we can set a default value in the constructor
    uploading: boolean;
    uploadMode: "" | "incremental" | "complete";

    // Mass Upload modal variables
    uploadStatus: "none" | "success" | "failed";
    massUploadErrorMessage: string;
    massUploadFileName: string;
    massUploadTableData: ExcelRowValidationResultDTO[];
    showModal: boolean;
}

class Users extends React.Component<UsersProps, UsersState> {
    private dataTableRef: DataTable<UserDTO>;
    private selectRef: Select;

    constructor(props: UsersProps) {
        super(props);
        bindAllOfThis(this, Users.prototype);

        this.state = {
            tableRecords: [],
            loading: true,
            isAdminTable: this.props.match && this.props.match.params.isAdminTable === 'true' ? true : false, // this is an abomination, but it works
            classroomId: this.props.classroomId ? this.props.classroomId : 0,
            onlyClassroomUsers: this.props.onlyClassroomUsers ? this.props.onlyClassroomUsers : false,
            uploading: false,
            uploadMode: "",
            uploadStatus: "none",
            massUploadErrorMessage: "",
            massUploadFileName: "",
            massUploadTableData: [],
            showModal: false,
        };
    }

    private getStudentReport(userId: number, reportName: string): void {
        ReportApiController.getStudentReport(userId).then(result => {
            message.success(this.props.translate('admin.users.messageReport'));
            downloadFile(result.data, reportName);
        });
    }

    private deactivateUser(userId: number): void {
        UserApiController.deactivateUser(userId).then(result => {
            message.success(this.props.translate('admin.users.messageDeactivate'));
            this.dataTableRef.refresh();
        }).catch(e => {
            message.error(this.props.translate('admin.users.messageDeactivateError'));
        });
    }

    private removeUserFromClassroom(userId: number, classroomId: number): void {
        UserApiController.removeUserFromClassroom(userId, classroomId).then(result => {
            message.success(this.props.translate('admin.users.messageRemove'));
            this.props.updateShouldRefreshTable();
        }).catch(e => {
            message.error(this.props.translate('admin.users.messageRemoveError'));
        });
    }

    private addUserToClassroom(userId: number, classroomId: number): void {
        UserApiController.addUserToClassroom(userId, classroomId).then(result => {
            if (result.data.isSuccess) {
                message.success(this.props.translate('admin.users.messageAdd'));
                this.props.updateShouldRefreshTable();
            }
            else {
                message.error(result.data.errorMessage);
            }
        }).catch(e => {
            message.error(this.props.translate('admin.users.messageAddError'));
        });
    }

    private fetchData(requestState: TableRequestDTO, checkEcho: () => boolean, callback: (response: TableResponseDTO<UserDTO>) => void): void {
        this.setState({
            loading: true
        });

        UserApiController.getUserTableData(requestState, this.state.isAdminTable, this.state.classroomId, this.state.onlyClassroomUsers)
            .then(results => {
                if (!checkEcho()) {
                    return;
                }

                let tableRecords = results.data.results ? results.data.results : [];

                tableRecords.forEach(z => z.userType = this.props.translate(`report.admin.roleFilter.${z.userType}`).toString());

                this.setState({
                    tableRecords: tableRecords,
                    loading: false
                });

                callback(results.data);
            });
    }

    private customTableColumns(): DataTableColumnProps<UserDTO>[] {
        let statementsLink = (text, record: UserDTO) => Routes.getRoute(Routes.ADMIN_STUDENT_STATEMENTS, this.state.classroomId, record.userId);

        let editLink = (text, record: UserDTO) => {
            if ((record.isCleverEnabledDistrict || record.isClassLinkEnabledDistrict) && !record.allowEditChangePassword) {
                return "";
            } else {
                return Routes.getRoute(Routes.ADMIN_ADD_EDIT_USER, this.state.classroomId, record.userId, this.state.isAdminTable);
            }
        };

        let changePasswordLink = (text, record: UserDTO) => {
            if ((record.isCleverEnabledDistrict || record.isClassLinkEnabledDistrict) && !record.allowEditChangePassword) {
                return "";
            } else {
                return Routes.getRoute(Routes.ADMIN_RESET_PASSWORD,
                    this.state.classroomId,
                    record.userId,
                    this.state.isAdminTable);
            }
        };


        // Base columns
        let columns = [
            DataTable.StandardColumns.Text('admin.users.columnHeader1', 'firstName'),
            DataTable.StandardColumns.Text('admin.users.columnHeader2', 'lastName')
        ];

        if (this.state.isAdminTable) {
            columns.push(DataTable.StandardColumns.Text('admin.users.columnHeader4', 'email'));
            columns.push(DataTable.StandardColumns.Text('admin.users.columnHeader10', 'userType'));
            columns.push(
                DataTable.AdvancedColumns.Enum(
                    <Translate id='admin.universalUserSearch.columnHeader8' />,
                    'isActive',
                    ['Active', 'Inactive'],
                    undefined,
                    (text, record, index) => (record.isActive ? "Active" : "Inactive")
                ),

            );
        }
        else {

            // student table
            columns.push(DataTable.StandardColumns.Text('admin.users.columnHeader11', 'grade'));
            // always allow searching by isActive
            columns.push(
                DataTable.AdvancedColumns.Enum(
                    <Translate id='admin.universalUserSearch.columnHeader8' />,
                    'isActive',
                    ['Active', 'Inactive'],
                    undefined,
                    (text, record, index) => (record.isActive ? "Active" : "Inactive")
                ),
            );

            // Only district admin will see school name
            if (this.props.User.roleID === Roles.DistrictAdmin.id) {
                columns.push(DataTable.StandardColumns.Text('admin.users.columnHeader9', 'schoolName'));
            }
            columns.push(DataTable.StandardColumns.Link('admin.users.columnHeader3', 'iStatements', statementsLink, 'profile'));
        }


        if (!this.state.isAdminTable) {
            columns.push({
                columnProps: {
                    key: 'report',
                    align: 'center',
                    width: 35,
                    render: (text, record, index) => {
                        let reportName = record.firstName + record.lastName + '.xlsx';
                        return (
                            <Tooltip placement="bottom" title={this.props.translate('admin.users.columnHeader8')} arrowPointAtCenter>
                                <Button shape="circle" icon="download" onClick={() => this.getStudentReport(record.userId, reportName)} />
                            </Tooltip>
                        );
                    }
                }
            });
        }

        let defaultTooltipProps = {
            mouseLeaveDelay: 0.5,
            mouseEnterDelay: 0.2
        };

        columns.push(
            {
                columnProps: {
                    key: 'password',
                    align: 'center',
                    width: 35,
                    render: (text, record, index) => {
                        return (
                            <Tooltip placement="bottom" title={this.props.translate('admin.users.columnHeader7')} arrowPointAtCenter mouseEnterDelay {...defaultTooltipProps}>
                                <Link to={{ pathname: changePasswordLink(text, record) }}>
                                    <Button shape="circle" icon="lock" disabled={(record.isCleverEnabledDistrict || record.isClassLinkEnabledDistrict) && !record.allowEditChangePassword} />
                                </Link>
                            </Tooltip>
                        );
                    }
                }
            },
            {
                columnProps: {
                    key: 'edit',
                    align: 'center',
                    width: 35,
                    render: (text, record, index) => {
                        let state: AddEditUserStateParams = { previousLocation: this.props.location.pathname };
                        return (
                            <Tooltip placement="bottom" title={this.props.translate('admin.common.editColumnHeader')} arrowPointAtCenter {...defaultTooltipProps}>
                                <Link to={{ pathname: editLink(text, record), state }}>
                                    <Button shape="circle" icon="edit" disabled={(record.isCleverEnabledDistrict || record.isClassLinkEnabledDistrict) && !record.allowEditChangePassword} />
                                </Link>
                            </Tooltip>
                        );
                    }
                }
            }
        );


        if (this.state.onlyClassroomUsers && this.state.classroomId) {
            // Only first name, last name, and minus button
            columns.splice(2, 4,
                {
                    columnProps: {
                        key: 'remove',
                        align: 'center',
                        width: 35,
                        render: (text, record, index) => {
                            return (
                                <Tooltip placement="bottom" title={this.props.translate('admin.users.columnHeader5')} arrowPointAtCenter {...defaultTooltipProps}>
                                    <Button shape="circle" icon="minus" onClick={() => this.removeUserFromClassroom(record.userId, this.state.classroomId)} />
                                </Tooltip>
                            );
                        }
                    }
                }
            );
        } else if (this.state.classroomId) {
            // Only first name, last name, and add button
            columns.splice(2, 4,
                {
                    columnProps: {
                        key: 'add',
                        align: 'center',
                        width: 35,
                        render: (text, record, index) => {
                            return (
                                <Tooltip placement="bottom" title={this.props.translate('admin.users.addToClass')} arrowPointAtCenter {...defaultTooltipProps}>
                                    <Button shape="circle" icon="plus" onClick={() => this.addUserToClassroom(record.userId, this.state.classroomId)} />
                                </Tooltip>
                            );
                        }
                    }
                });
        }

        return columns;
    }

    private getDataTableButtons(): DataTableButton[] {
        let addButtonTranslationId = this.state.isAdminTable ? "admin.users.addButton2" : "admin.users.addButton1";
        let addButton = new DataTableButton(addButtonTranslationId, this.handleRedirect);
        let resetButton = new DataTableButtonReset();
        let downloadActiveButton = new DataTableButton("admin.users.downloadActiveStudentsButtonText", this.handleDownloadActiveStudents);
        let downloadInactiveButton = new DataTableButton("admin.users.downloadInActiveStudentsButtonText", this.handleDownloadInActiveStudents);

        let currentUserRole = Roles.FindById(this.props.User.roleID);
        let isAdmin = this.state.isAdminTable;
        let isClever = this.props.User.isSsoEnabled;
        let isClassroom = this.state.classroomId > 0;
        let isInClassroom = this.state.onlyClassroomUsers;

        let hasAdd = false;
        let hasDownloadActive = false;
        let hasDownloadInactive = false;
        let hasReset = true; // There is no instance where reset is not available

        switch (currentUserRole) {
            case Roles.KuderAdmin:
                hasAdd = true;
                hasDownloadActive = false;
                hasDownloadInactive = false;
                break;
            case Roles.StateAdmin:
                hasAdd = true;
                hasDownloadActive = false;
                hasDownloadInactive = false;
                break;
            case Roles.DistrictAdmin:
                if (isClever) {
                    hasAdd = isAdmin;
                    hasDownloadActive = !isAdmin;
                    hasDownloadInactive = !isAdmin;
                } else {
                    hasAdd = true;
                    hasDownloadActive = !isAdmin;
                    hasDownloadInactive = !isAdmin;
                }
                break;
            case Roles.SchoolAdmin:
                if (isClever) {
                    hasAdd = isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                    hasDownloadActive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                    hasDownloadInactive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                } else {
                    hasAdd = isInClassroom && isClassroom || !isInClassroom && !isClassroom;
                    hasDownloadActive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                    hasDownloadInactive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                }
                break;
            case Roles.TeacherCounselorAdmin:
                if (isClever) {
                    hasAdd = false;
                    hasDownloadActive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                    hasDownloadInactive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                } else {
                    hasAdd = true;
                    hasDownloadActive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                    hasDownloadInactive = !isAdmin && (isInClassroom && isClassroom || !isInClassroom && !isClassroom);
                }
                break;
            default:
                break;
        }

        return [
            hasAdd ? addButton : null,
            hasDownloadActive ? downloadActiveButton : null,
            hasDownloadInactive ? downloadInactiveButton : null,
            hasReset ? resetButton : null,
        ].filter(x => x); // This removes nulls
    }

    private handleDownloadActiveStudents(): void {
        window.location.href = "/" + UserApiController.RouteGetAllActiveInactiveStudents(true);
    }

    private handleDownloadInActiveStudents(): void {
        window.location.href = "/" + UserApiController.RouteGetAllActiveInactiveStudents(false);
    }

    private handleRedirect(): void {
        let redirectUrl = Routes.getRoute(Routes.ADMIN_ADD_EDIT_USER, this.state.classroomId, 0, this.state.isAdminTable);
        let state: AddEditUserStateParams = { previousLocation: this.props.location.pathname };
        this.props.history.push(redirectUrl, state);
    }

    private downloadMassStudentUploadTemplate() {
        window.location.href = "/" + UserApiController.RouteGetMassStudentUploadTemplate();
    }

    private handleBeforeUpload(file: RcFile, fileList: RcFile[]) {
        this.setState({ uploading: true, uploadStatus: "none", massUploadErrorMessage: "" });
        return true;
    }

    private handleUploadOnChange(info: UploadChangeParam) {
        if (info.file.status !== 'uploading') {
            console.log(info.file, info.fileList);
            this.setState({ uploading: false, uploadMode: "" });
        } else {
            return;
        }

        let response = (info.file.response || {}) as GenericBaseResponseDTO<ExcelRowValidationResultDTO[]>;
        // Get error from response. If it's a really bad error (500), then response will actually have a message attached to it rather than a proper response
        let error = info.file.response != null && info.file.response.message != null ? info.file.response.message : response.errorMessage;

        if (info.file.status === 'done' && response.isSuccess) {
            message.success(info.file.name + " - " + this.props.translate('admin.users.messageUpload'));
            this.setState({
                uploadStatus: "success",
                massUploadFileName: info.file.name,
                massUploadErrorMessage: error,
                massUploadTableData: response.data
            });
            this.dataTableRef.refresh();
        } else {
            message.error(info.file.name + " - " + this.props.translate('admin.users.messageUploadError'));
            this.setState({
                uploadStatus: "failed",
                massUploadFileName: info.file.name,
                massUploadErrorMessage: error,
                massUploadTableData: response.data
            });
        }
    }

    private showErrorsModal() {
        // Super simple method, yes, but react likes 'constant' functions like this when trying to determine if it should re-render or not
        this.setState({ showModal: true });
    }

    private handleModelClose() {
        this.setState({ showModal: false });
    }

    private handleUploadModeChange(value: "incremental" | "complete") {
        this.setState({
            uploadMode: value,
            uploadStatus: "none"
        });
    }

    private renderUploadCompleteIcon(): JSX.Element {
        if (this.state.uploadStatus === "success") {
            return (<Icon type="check-circle" theme="twoTone" twoToneColor="#52c41a" />);
        }

        if (this.state.uploadStatus === "failed") {
            return <Button onClick={this.showErrorsModal}><Icon type="exclamation-circle" theme="twoTone" twoToneColor="#c41a1a" /> View Upload Errors</Button>;
        }
    }

    private renderMassStudentUpload(): JSX.Element {
        const uploadProps = {
            name: 'file',
            showUploadList: false,
            action: "/" + UserApiController.RouteMassStudentUploadV2(),
            disabled: this.state.uploadMode === "",
            beforeUpload: this.handleBeforeUpload,
            onChange: this.handleUploadOnChange,
            data: { isCompleteMode: this.state.uploadMode === "complete" }
        };

        if (!this.state.isAdminTable && !this.state.classroomId && !this.props.User.isSsoEnabled) {
            return (
                <div className="mass-upload-buttons">
                    <div>
                        <span>
                            <Translate id="admin.users.massStudentUploadStep" /> 1:
                        </span>
                        <Button onClick={this.downloadMassStudentUploadTemplate}>
                            <Icon type="download" /> <Translate id="admin.users.massStudentUploadTemplate" />
                        </Button>
                    </div>
                    <div>
                        <span>
                            <Translate id="admin.users.massStudentUploadStep" /> 2:
                        </span>
                        <span>
                            <Translate id="admin.users.massStudentUploadStep2Content" />
                        </span>
                    </div>
                    <div>
                        <span>
                            <Translate id="admin.users.massStudentUploadStep" /> 3:
                        </span>
                        <Select
                            ref={ref => this.selectRef = ref}
                            placeholder={this.props.translate('admin.users.uploadMode')}
                            onChange={this.handleUploadModeChange}
                            value={this.state.uploadMode}
                            style={{ width: 200 }}>
                            <Select.Option value="incremental">{this.props.translate('admin.users.incremental')}</Select.Option>
                            <Select.Option value="complete">{this.props.translate('admin.users.complete')}</Select.Option>
                        </Select>
                    </div>
                    <div>
                        <span>
                            <Translate id="admin.users.massStudentUploadStep" /> 4:
                        </span>
                        <Upload {...uploadProps}>
                            <Button disabled={this.state.uploadMode === "" || this.state.uploading }>
                                <Icon type="upload" /> <Translate id="admin.users.massStudentUpload" />
                            </Button>
                        </Upload>
                        <Spin spinning={this.state.uploading} />
                        {this.renderUploadCompleteIcon()}
                    </div>
                </div>
            );
        }
    }

    private renderTableTitle(): JSX.Element {
        let headerTranslationId = this.state.isAdminTable ?
            'admin.users.tableHeader1' : this.state.onlyClassroomUsers ?
                'admin.users.tableHeader3' :
                'admin.users.tableHeader2';

        return <h2><Translate key="table-title" id={headerTranslationId} /></h2>;
    }

    private renderTextForCleverEnabledDistrict(): JSX.Element {
        if (this.props.User.isSsoEnabled && !this.state.isAdminTable) {
            return <h3><Translate key="clever-text" id="admin.users.cleverEnabledDistrictText" /></h3>;
        }
    }

    render() {
        if (this.props.shouldRefreshTable) {
            this.dataTableRef.refresh();
            this.props.updateShouldRefreshTable();
        }

        return (
            <div className="admin-student">
                {this.renderTableTitle()}
                {this.renderTextForCleverEnabledDistrict()}
                {this.renderMassStudentUpload()}
                <UsersUploadErrorModal
                    errorMessage={this.state.massUploadErrorMessage}
                    fileName={this.state.massUploadFileName}
                    tableData={this.state.massUploadTableData}
                    visible={this.state.showModal}
                    onOkay={this.handleModelClose}
                />
                <DataTable
                    ref={ref => this.dataTableRef = ref}
                    serverSide={true}
                    tableProps={{ rowKey: 'userId', loading: this.state.loading }}
                    columns={this.customTableColumns()}
                    data={this.state.tableRecords}
                    fetchData={this.fetchData}
                    buttonBar={this.getDataTableButtons()} />
            </div>
        );
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User,
    };
}

export default connect(mapStateToProps)(withLocalize(Users));



// WEBPACK FOOTER //
// ./src/pages/admin/Users.tsx